import React from "react";
import { Avatar, Button, Grid, Typography } from "@sumprio/ui";
import { Edit2Icon, TrashIcon } from "@sumprio/ui/icons";
import { SafeTooltipTrigger as TooltipTrigger } from "components/common/SafeTooltipTrigger";
import type { FeedItem } from "./types";
import { getInitials } from "./utils";

export interface NoteFeedItemProps extends FeedItem {
  avatarSize?: "sm" | "md";
  onEdit?: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
}

export function NoteFeedItem({
  name,
  initials: initialsProp,
  message,
  timestamp,
  onEdit,
  onDelete,
  isEditing = false,
}: NoteFeedItemProps) {
  const initials = initialsProp ?? getInitials(name, "??");
  const showActions = Boolean(onEdit || onDelete);

  return (
    <Grid display="flex" flexDirection="row" gap="md" width="100%">
      <Grid display="flex" flexDirection="column" alignItems="center" style={{ flexShrink: 0 }}>
        <Avatar type="initial" size="sm" initials={initials} />
      </Grid>
      <Grid display="flex" flexDirection="column" gap="xs" style={{ minWidth: 0, flex: 1 }}>
        <Grid
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="sm"
          width="100%"
        >
          <Typography variant="body" bodySize="sm" bodyWeight="medium" color="secondary">
            {name} {timestamp}
          </Typography>
          {showActions ? (
            <Grid display="flex" alignItems="center" gap="xs" style={{ flexShrink: 0 }}>
              {onEdit ? (
                <TooltipTrigger tooltip="Edit note" placement="top" theme="dark">
                  <Button
                    iconOnly
                    icon={Edit2Icon}
                    variant="brand-tertiaryColor"
                    size="xs"
                    aria-label="Edit note"
                    onClick={onEdit}
                  />
                </TooltipTrigger>
              ) : null}
              {onDelete ? (
                <TooltipTrigger tooltip="Delete note" placement="top" theme="dark">
                  <Button
                    iconOnly
                    icon={TrashIcon}
                    variant="danger-tertiary"
                    size="xs"
                    aria-label="Delete note"
                    onClick={onDelete}
                  />
                </TooltipTrigger>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
        <Grid
          display="flex"
          flexDirection="column"
          style={{
            padding: "10px 12px",
            borderRadius: 8,
            backgroundColor: isEditing
              ? "var(--color-background-brand-primary-subtle, #fef6f4)"
              : "var(--color-background-neutral-secondary, #f5f5f5)",
            alignSelf: "flex-start",
            maxWidth: "100%",
            outline: isEditing ? "1px solid var(--color-border-brand-primary, #f35120)" : undefined,
          }}
        >
          <Typography variant="body" bodySize="sm" bodyWeight="regular">
            {message}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
